import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconEdit,
  IconPlus,
  IconTrash,
  IconUserPlus,
  IconStar,
  IconStarFilled,
  IconCircleX
} from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, InputGroup, Offcanvas, Row, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { optional, z } from "zod";
import Select from "react-select";
import validator from "validator";
import { baseURL } from "../Utils/constants/constant";
import { toast } from "sonner";
import { GetRequest, PostRequest, PutRequest } from "../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { getCities, getStates, getCountries } from "../Utils/hooks/location";
import { useNavigate } from "react-router-dom";
import {
  checkDuplicate,
  useDebounce,
  checkCompanyDuplicate,
  customStyles
} from "../Utils/helpers/commonhelper";
import {
  Date,
  DateField,
  MultipleOpt,
  PhoneNumberInput,
  SelectOpt,
  Text,
  Time,
} from "./FieldComp";
import { parsePhoneNumber } from "libphonenumber-js/min";
import { usePostRequest } from "../Utils/hooks/QueryHandler";
import { de } from "zod/v4/locales";
import { CancelBtn } from "./Buttons/CancelBtn";
import { SubmitBtn } from "./Buttons/SubmitBtn";

// Industry options
const industryOptions = [
  { value: "Technology", label: "Technology" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Finance", label: "Finance" },
  { value: "Education", label: "Education" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Retail", label: "Retail" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Construction", label: "Construction" },
  { value: "Transportation", label: "Transportation" },
  { value: "Food & Beverage", label: "Food & Beverage" },
  { value: "Entertainment", label: "Entertainment" },
  { value: "Consulting", label: "Consulting" },
  { value: "Marketing & Advertising", label: "Marketing & Advertising" },
  { value: "Legal Services", label: "Legal Services" },
  { value: "Insurance", label: "Insurance" },
  { value: "Telecommunication", label: "Telecommunication" },
  { value: "Energy", label: "Energy" },
  { value: "Agriculture", label: "Agriculture" },
  { value: "Automotive", label: "Automotive" },
  { value: "Aerospace", label: "Aerospace" },
  { value: "Pharmaceutical", label: "Pharmaceutical" },
  { value: "Banking", label: "Banking" },
  { value: "E-commerce", label: "E-commerce" },
  { value: "Media", label: "Media" },
  { value: "Non-profit", label: "Non-profit" },
  { value: "Government", label: "Government" },
  { value: "Hospitality", label: "Hospitality" },
  { value: "Travel & Tourism", label: "Travel & Tourism" },
  { value: "Sports & Recreation", label: "Sports & Recreation" },
  { value: "Beauty & Wellness", label: "Beauty & Wellness" },
];
const businessTypeOptions = [
  { value: "Private Limited Company", label: "Private Limited Company" },
  { value: "Public Limited Company", label: "Public Limited Company" },
  { value: "LLP", label: "Limited Liability Partnership" },
  { value: "PROPRIETORSHIP", label: "Proprietorship" },
];

const employeeSizeOptions = [
  { value: "1-10", label: "1-10" },
  { value: "11-50", label: "11-50" },
  { value: "51-200", label: "51-200" },
  { value: "201-500", label: "201-500" },
  { value: "501-1000", label: "501-1000" },
  { value: "1001+", label: "1001+" },
];
// Star Rating Component
const StarRating = ({ rating, onRatingChange }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="d-flex align-items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className="border-0 bg-transparent p-0"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRatingChange(star)}
        >
          {star <= (hoverRating || rating) ? (
            <IconStarFilled size={24} color="#ffc107" />
          ) : (
            <IconStar size={24} color="#dee2e6" />
          )}
        </button>
      ))}
      <span className="ms-2 text-muted">
        ({rating} star{rating !== 1 ? "s" : ""})
      </span>
    </div>
  );
};

const optionalUrl = (label) =>
  z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val || val === "") return true; // empty allowed
        if (val.includes(" ")) return false;
        return /^https?:\/\/.+/.test(val);
      },
      { message: `Invalid ${label} URL` }
    );
// Zod schema for form validation
const companySchema = z.object({
  company_name: z.string().min(1, "Company name is Required"),
  website: optionalUrl("Website"),
  industry: z.string().optional().nullable(),
  business_type: z.string().optional(),
  employee_size: z.string().nullable().optional(),
  owner_id: z.string().min(1, "Owner name is required"),
  address: z.string().optional().nullable(),
  country: z.string().optional(),
  state: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  zipcode: z.string().optional().nullable(),
  facebook: optionalUrl("Facebook"),
  twitter: optionalUrl("Twitter"),
  instagram: optionalUrl("Instagram"),
  linkedin: optionalUrl("LinkedIn"),
  rating: z.string().optional(),
  about: z.string().nullable().optional(),
});

// Email validation function
const isValidEmail = (email) => {
  return validator.isEmail(email) && email.toLowerCase().includes(".com");
};

// Mobile number validation function (exactly 10 digits)
const isValidMobileNumber = (phone) => {
  const cleanPhone = phone.replace(/\D/g, "");
  return cleanPhone.length === 10;
};

const EditCompanyOffcanvas = ({
  show,
  setShow,
  company_data,
  fetchData,
  allContacts,
  refetch,
  setFindData,
  isCloneClicked,
  setIsCloneClicked,
  isOwnerSame,
  findData,
}) => {
  // State management
  const [leadOwner, setLeadOwner] = useState([])
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  })
  const navigate = useNavigate()

  // Authentication headers
  const customerId = customer?.customer_details?.customer_id
  const token = customer.token.authorization
  const rf = customer.token.authentication
  const extras = { customer, setCustomer, rf, navigate }
  const headers = {
    authentication: rf,
    authorization: token,
  }

  // Rating state
  const [rating, setRating] = useState(
    Number.parseInt(company_data?.rating) || 4,
  )
  // groped state to hold the dynamic group data
  const [grouped, setGrouped] = useState([])
  // state to store all dynamic fields
  const [dynamicFields, setDynamicFields] = useState([])

  // Initial data fetching state for edit initialization only
  const [isDataFetching, setIsDataFetching] = useState(false)

  // ungroped state to hold the dynamic group data
  const [ungrouped, setUnGrouped] = useState([])

  // Memoize the combined schema to avoid unnecessary recalculations
  // Form handling
  const generateDynamicSchema = (fields) => {
    const schemaFields = {}

    fields.forEach((field) => {
      const fieldId = field.id

      switch (field.label_type) {
        case "text":
        case "largeText":
        case "address":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional()
          break

        case "numerical":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)

              : z.string().optional()
          break

        case "monetary":
          schemaFields[fieldId] = field.is_required === "yes"
            ? z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
              z.number({
                required_error: `${field.label_name} is required`,
                invalid_type_error: "Please enter a valid number"
              })
                .min(0.01, "Amount must be positive")
            )
            : z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
              z.number().min(0, "Amount cannot be negative").optional().nullable()
            );
          break;

        case "single-option":
        case "user":
        case "person":
        case "organization":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;

        case "date":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional()
          break

        case "date range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z
                .object({
                  start: z.string().min(1, "Start date is required"),
                  end: z.string().min(1, "End date is required"),
                })
                .refine((data) => data.start <= data.end, {
                  message: "End date must be after start date",
                })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional()
          break

        case "time":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional()
          break

        case "time-range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.object({
                start: z.string().min(1, "Start time is required"),
                end: z.string().min(1, "End time is required"),
              })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional()
          break

        case "phone":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string(z.string().min(1, "phone is requ"))
              : z.string().optional()
          break
        case "multiple-option": // Added multiple-option schema
          schemaFields[fieldId] = // Changed from fieldName
            field.is_required === "yes"
              ? z.array(z.string()).min(1, `${field.label_name} is required`)
              : z.array(z.string()).optional()
          break

        // case 'phone_multiple':
        //     schemaFields[fieldName] = field.is_required === "yes"
        //         ? z.array(z.object({
        //             phone: z.string().min(1, "Phone number is required"),
        //             phone_type: z.string(),
        //             country_code: z.string(),
        //             countryName: z.string()
        //         })).min(1, "At least one phone number is required")
        //         : z.array(z.object({
        //             phone: z.string(),
        //             phone_type: z.string(),
        //             country_code: z.string(),
        //             countryName: z.string()
        //         })).optional();
        //     break;

        default:
          schemaFields[fieldId] = z.any().optional()
      }
    })

    return z.object(schemaFields)
  }

  const finalSchema = useMemo(() => {
    if (dynamicFields.length === 0) {
      return companySchema
    }

    // Combine grouped and ungrouped fields
    const allDynamicFields = [
      ...(grouped?.flatMap((g) => g.groupFields || []) || []),
      ...(ungrouped || []),
    ]

    if (allDynamicFields.length === 0) {
      return companySchema
    }

    const dynamicSchema = generateDynamicSchema(allDynamicFields)

    // Merge static and dynamic schemas
    return companySchema.merge(dynamicSchema)
  }, [grouped, ungrouped, dynamicFields])

  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: zodResolver(finalSchema),
    defaultValues: {
      ...company_data,
      country: company_data.country || "99", // Default to India if no country
      state: company_data.state,
      city: company_data.city,
      about: company_data.about ?? "",
    },
  })

  useEffect(() => {
    if (company_data?.company_name) {
      const baseName = company_data.company_name
      const updatedName = isCloneClicked ? `${baseName} clone` : baseName

      setValue("company_name", updatedName)
    }
  }, [isCloneClicked, company_data, setValue])

  // Image state

  const [image, setImage] = useState()

  // Contact information states
  const [emails, setEmails] = useState(
    isCloneClicked
      ? [{ email: "", email_type: "Office" }]
      : company_data?.user_email
        ? JSON.parse(company_data.user_email)
        : [{ email: "", email_type: "Office" }],
  )

  const [alternateEmails, setAlternateEmails] = useState(
    isCloneClicked
      ? []
      : company_data?.alternate_email
        ? JSON.parse(company_data.alternate_email)
        : [],
  )
  const [contacts, setContacts] = useState(
    isCloneClicked
      ? [{ phone: "", phone_type: "Mobile", country_code: '' }]
      : company_data?.user_phone
        ? JSON.parse(company_data.user_phone)
        : [{ phone: "", phone_type: "Mobile", country_code: "" }],
  )
  const [alternateContacts, setAlternateContacts] = useState(
    company_data?.alternate_phone
      ? JSON.parse(company_data.alternate_phone)
      : [],
  )
  const companyName = watch("company_name") // <--- Define companyName using watch
  const debounceCompany = useDebounce(companyName || "", 500) // <--- Use the defined companyName

  // Error states
  const [emailErrors, setEmailErrors] = useState([])
  const [contactErrors, setContactErrors] = useState([])
  const [emailValidationErrors, setEmailValidationErrors] = useState([])
  const [contactValidationErrors, setContactValidationErrors] = useState([])
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null)
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null)
  const [duplicateCompanyMessage, setDuplicateCompanyMessage] = useState(null)
  const debouncedEmail = useDebounce(emails[0]?.email || "", 500)
  const debouncedPhone = useDebounce(contacts[0]?.phone || "", 500)
  const [activeSection, setActiveSection] = useState("")

  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState(
    company_data.timezone ? JSON.parse(company_data.timezone) : {},
  )

  const validatePhoneNumber = (rawNumber, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        String(rawNumber),
        String(countryCode?.toUpperCase()),
      )
      const valid = phoneNumber?.isValid() || false
      return valid
    } catch (error) {
      return false
    }
  }
  // Location states
  const [country, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [city, setCity] = useState([])

  // React-select state
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [showOnlyRequired, setShowOnlyRequired] = useState(true);
  // Helper function to scroll to form sections
  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    const formContainer = document.querySelector(".form-container")

    if (element && formContainer) {
      formContainer.scrollTo({
        top: element.offsetTop - 110,
        behavior: "smooth",
      })
      setActiveSection(id) // <-- Track which was clicked
    }
  }

  // Email field handlers
  const handleAddEmail = () => {
    setEmails([...emails, { email: "", email_type: "Office" }])
  }

  const handleDeleteEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index)
    setEmails(newEmails)
    validateEmails(newEmails)
  }

  // Contact field handlers
  const handleAddContact = () => {
    setContacts([
      ...contacts,
      { phone: "", phone_type: "Mobile", country_code: "+91" },
    ])
  }

  const handleDeleteContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index)
    setContacts(newContacts)
    validateContacts(newContacts)
  }

  // Validation functions
  const validateEmails = (emailList) => {
    const duplicates = emailList.reduce((acc, currentEmail, currentIndex) => {
      const isDuplicate = emailList.some(
        (email, otherIndex) =>
          currentIndex !== otherIndex &&
          currentEmail.email &&
          email.email &&
          currentEmail.email.toLowerCase() === email.email.toLowerCase(),
      )
      if (isDuplicate) acc.push(currentIndex)
      return acc
    }, [])
    setEmailErrors(duplicates)
    // Validate email format and .com requirement
    const validationErrors = emailList.reduce(
      (acc, currentEmail, currentIndex) => {
        if (currentEmail.email && !isValidEmail(currentEmail.email)) {
          acc.push(currentIndex)
        }
        return acc
      },
      [],
    )
    setEmailValidationErrors(validationErrors)
  }

  const validateContacts = (contactList) => {
    const duplicates = contactList.reduce(
      (acc, currentContact, currentIndex) => {
        const isDuplicate = contactList.some(
          (contact, otherIndex) =>
            currentIndex !== otherIndex &&
            currentContact.phone &&
            contact.phone &&
            currentContact.country_code &&
            contact.country_code &&
            `${currentContact.country_code}${currentContact.phone.replace(
              /\D/g,
              "",
            )}` ===
            `${contact.country_code}${contact.phone.replace(/\D/g, "")}`,
        )
        if (isDuplicate) acc.push(currentIndex)
        return acc
      },
      [],
    )
    setContactErrors(duplicates)
    // Validate mobile number (exactly 10 digits)
    const validationErrors = contactList.reduce(
      (acc, currentContact, currentIndex) => {
        if (
          currentContact.phone &&
          !isValidMobileNumber(currentContact.phone)
        ) {
          acc.push(currentIndex)
        }
        return acc
      },
      [],
    )
    setContactValidationErrors(validationErrors)
  }

  // Field change handlers
  const handleEmailChange = (field, value, index) => {
    const newEmails = [...emails]
    newEmails[index] = { ...newEmails[index], [field]: value }
    setEmails(newEmails)
    validateEmails(newEmails)
  }

  const handleContactChange = (field, value, index, details) => {
    const newContacts = [...contacts]
    newContacts[index] = { ...newContacts[index], [field]: value, ...details }
    setContacts(newContacts)
    validateContacts(newContacts)
  }

  // React-select handlers
  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption)
    setValue("country", selectedOption ? selectedOption.value.toString() : "")
    setSelectedState(null)
    setSelectedCity(null)
    setValue("state", "")
    setValue("city", "")
  }

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption)
    setValue("state", selectedOption ? selectedOption.value.toString() : "")
    setSelectedCity(null)
    setValue("city", "")
  }

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption)
    setValue("city", selectedOption ? selectedOption.value.toString() : "")
  }

  // Rating handler
  const handleRatingChange = (newRating) => {
    setRating(newRating)
    setValue("rating", newRating.toString())
  }
  useEffect(() => {
    if (company_data?.employee_size) {
      setValue("employee_size", company_data.employee_size)
    }
  }, [company_data, setValue])

  // UseEffect for email duplication check
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail) {
        setDuplicateEmailMessage(null)
        const isEmailExisting = await checkDuplicate({
          col: "email",
          customer_id: customer.customer_details?.customer_id,
          validating_val: debouncedEmail,
          contact_id: company_data?.id || "",
          checkFor: "company",
          existing_val: company_data.email || "",
          // Exclude current company
        })
        if (isEmailExisting) {
          setDuplicateEmailMessage("Email already exists!")
        }
      } else {
        setDuplicateEmailMessage(null)
      }
    }
    checkEmail()
  }, [debouncedEmail, company_data?.id])
  // UseEffect for phone duplication check
  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone) {
        setDuplicatePhoneMessage(null)
        const isPhoneExisting = await checkDuplicate({
          col: "phone",
          customer_id: customer.customer_details?.customer_id,
          validating_val: debouncedPhone,
          contact_id: company_data?.id || "",
          checkFor: "company",
          existing_val: company_data.phone || "",

          // Exclude current company
        })
        if (isPhoneExisting) {
          setDuplicatePhoneMessage("Phone number already exists!")
        }
      } else {
        setDuplicatePhoneMessage(null)
      }
    }
    checkPhone()
  }, [debouncedPhone, company_data?.id])

  useEffect(() => {
    const checkCompany = async () => {
      if (debounceCompany && debounceCompany !== company_data?.company_name) {
        setDuplicateCompanyMessage(null)
        const isCompanyNameExisting = await checkCompanyDuplicate({
          customer_id: customer.customer_details?.customer_id,
          validating_val: debounceCompany,
          contact_id: company_data?.id || "",
          checkFor: "company",

          // Exclude current company
        })
        if (isCompanyNameExisting) {
          setDuplicateCompanyMessage("Company already exists!")
        }
      } else {
        setDuplicateCompanyMessage(null)
      }
    }
    checkCompany()
  }, [debounceCompany, company_data?.company_name, company_data?.id])
  // Fetch countries on mount
  useEffect(() => {
    ; (async () => {
      try {
        const data = await getCountries()
        if (!data) return
        // Update state once with all country data
        const countryOptions = data.map((country) => ({
          value: country.id,
          label: country.name,
        }))
        setCountries(countryOptions)
        // Set initial selected country logic
        let initialCountry = null
        const countryToFind = company_data.country || "99" // Default to India if no country
        // Find the country in options
        initialCountry = countryOptions.find(
          (c) => c.value.toString() === countryToFind.toString(),
        )
        // If not found, try to find India as fallback
        if (!initialCountry) {
          initialCountry = countryOptions.find(
            (c) => c.value === 99 || c.label === "India",
          )
        }
        if (initialCountry) {
          setSelectedCountry(initialCountry)
          setValue("country", initialCountry.value.toString())
        }
      } catch (error) {
        toast.error("Error Fetching Countries")
        return
      }
    })()
  }, [company_data.country, setValue])

  // useeffect for state
  const countryId = watch("country")
  const stateId = watch("state")
  useEffect(() => {
    ; (async () => {
      try {
        if (dirtyFields.country) {
          setValue("state", "")
          setValue("city", "")
        }
        // Use selected country value, company data country, or default to India (99)
        const targetCountryId = selectedCountry
          ? selectedCountry.value
          : company_data.country || 99
        const data = await getStates(targetCountryId)
        if (!data || data === undefined) {
          setStates([])
          setCity([])
          setValue("state", "")
          setValue("city", "")
          return
        }
        if (stateId === " " || stateId === "0") setValue("state", "")
        // Update state once with all state data
        const stateOptions = data.map((state) => ({
          value: state.id,
          label: state.state_name,
        }))
        setStates(stateOptions)
        // Set initial selected state if exists
        if (company_data.state && !dirtyFields.country) {
          const initialState = stateOptions.find(
            (s) => s.value.toString() === company_data.state.toString(),
          )
          setSelectedState(initialState || null)
        }
        // Clear cities when states change
        if (dirtyFields.state) {
          setCity([])
          setValue("city", "")
        }
      } catch (error) {
        toast.error("Error Fetching State")
        return
      }
    })()
  }, [selectedCountry, company_data.state, company_data.country])

  // city useeffect
  useEffect(() => {
    ; (async () => {
      try {
        if (!selectedState && !company_data.state) {
          setCity([])
          setValue("city", "")
          return
        }
        // Clear city when state changes
        if (dirtyFields.state) {
          setValue("city", "")
        }
        const data = await getCities(
          selectedState ? selectedState.value : company_data.state,
        )
        if (!data || data === undefined) {
          setCity([])
          setValue("city", "")
          return
        }
        // Update city options
        const cityOptions = data.map((city) => ({
          value: city.id,
          label: city.city_name,
        }))
        setCity(cityOptions)
        // Set initial selected city if exists
        if (company_data.city && !dirtyFields.state && !dirtyFields.country) {
          const initialCity = cityOptions.find(
            (c) => c.value.toString() === company_data.city.toString(),
          )
          setSelectedCity(initialCity || null)
        }
      } catch (error) {
        toast.error("Error Fetching Cities")
        return
      }
    })()
  }, [selectedState, company_data.city])

  // Fetch lead owners
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const data = await GetRequest(
          `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
          { headers },
          {},
        )
        if (data?.data) {
          setLeadOwner(
            data.data?.map((user) => ({
              leadName: user.full_name,
              leadId: user.id,
            })),
          )
        }
      } catch (error) {
        console.error("Error fetching users:", error)
        toast.error("Failed to load users")
      }
    }
    fetchAllUsers()
  }, [])

  // fetching dynamic fields for the contact form
  useEffect(() => {
    (async () => {
      setIsDataFetching(true)
      try {
        const res = await GetRequest(
          `company/fetchCustomFields/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate },
        )
        if (res.success === false || res.success === "fail") {
          toast.error(res.message)
          return
        }
        const fetchedGrouped = res.data?.grouped || []
        const fetchedUngrouped = res.data?.ungrouped || []

        setGrouped(fetchedGrouped)
        setUnGrouped(fetchedUngrouped)

        const allFields = [
          ...(fetchedGrouped?.flatMap((g) => g.groupFields || []) || []),
          ...(fetchedUngrouped || []),
        ]
        setDynamicFields(allFields)

        // IMPORTANT: Reset form with existing contact_data and dynamic field values
        // This ensures the form is re-hydrated after dynamic fields are loaded and schema is updated
        const dynamicFieldValues = {}

        // First, populate dynamicFieldValues from contact_data.custom_fields
        if (company_data.custom_fields) {
          company_data.custom_fields?.forEach((customField) => {
            const fieldId = customField.field_id
            let fieldValue = customField.value

            const parseVal = JSON.parse(fieldValue)
            dynamicFieldValues[fieldId] = parseVal
          })
        }
        // Then, iterate through all fetched dynamic fields to set default empty values
        // for any fields not present in contact_data.custom_fields
        allFields.forEach((field) => {
          if (dynamicFieldValues[field.id] === undefined) {
            if (
              field.label_type === "date range" ||
              field.label_type === "time-range"
            ) {
              dynamicFieldValues[field.id] = { start: "", end: "" }
            } else if (field.label_type === "multiple-option") {
              dynamicFieldValues[field.id] = []
            } else {
              dynamicFieldValues[field.id] = ""
            }
          }
        })

        // The `reset` call remains the same, using the now correctly populated `dynamicFieldValues`
        reset({
          ...company_data,
          visible_to: company_data.owner_id,
          report_to: company_data.owner_id,
          company_id: company_data.company_id,
          lead_source_id: company_data.lead_source_id,
          state: company_data.state,
          city: company_data.city,
          report_to: company_data.report_to,
          ...dynamicFieldValues,
        })
      } catch (error) {
        toast.error("Internal Server Error")
        return
      } finally {
        setIsDataFetching(false)
      }
    })()
  }, [customer])

  // Form submission handler
  const handleFormSubmit = async (formData) => {
    try {
      // . Check duplicate company name (only if changed)
      if (formData.company_name !== company_data?.company_name) {
        const isDuplicate = await checkCompanyDuplicate({
          customer_id: customer.customer_details?.customer_id,
          validating_val: formData.company_name,
          contact_id: company_data?.id || "", // exclude current company
        })

        if (isDuplicate) {
          toast.error("Company with this name already exists!")
          return
        }
      }
      const invalidEmails = emails.some(
        (email) => email.email && !isValidEmail(email.email),
      )
      if (invalidEmails) {
        toast.error("Incorrect Email")
        return
      }

      if (emailErrors.length > 0) {
        toast.error("Please remove duplicate emails")
        return
      }

      if (contactErrors.length > 0) {
        toast.error("Please remove duplicate contacts")
        return
      }

      const custom_fields = []
      const staticFieldsPayload = { ...formData }
      dynamicFields.forEach((field) => {
        const fieldName = field.id
        if (fieldName && formData.hasOwnProperty(fieldName)) {
          let formattedValue = formData[fieldName]
          let isRangeField = false // Initialize flag for range property

          // Handle complex types for custom_fields payload
          if (
            field.label_type === "date range" ||
            field.label_type === "time-range"
          ) {
            isRangeField = true // Set flag for range fields
            // Ensure both start and end are present before sending
            if (
              !formattedValue ||
              (!formattedValue.start && !formattedValue.end)
            ) {
              formattedValue = { start: "", end: "" } // Send empty object if range is incomplete/empty
            }
          } else if (field.label_type === "multiple-option") {
            // Ensure it's an array
            if (!Array.isArray(formattedValue)) {
              formattedValue = [] // Send empty array if not an array
            }
          }
          const customFieldEntry = {
            field_id: field.id.toString(), // Ensure ID is a string
            value: formattedValue,
            range: isRangeField ? true : false,
          }
          custom_fields.push(customFieldEntry)
          // Remove dynamic field from static payload to avoid duplication
          delete staticFieldsPayload[fieldName]
        }
      })

      // 3. Prepare payload
      const editCompany = {
        ...formData,
        industry: formData?.industry || "",
        company_id: company_data?.id,
        email: emails[0]?.email || "",
        phone: contacts[0]?.phone || "",
        user_email: emails,
        user_phone: contacts,
        alternate_email: alternateEmails,
        alternate_phone: alternateContacts,
        timezone: selectedTimezone,
        company_image: image?.[0] || null,
        old_company_image: company_data?.company_image || null,
        business_type: formData.business_type || "",
        owner_id: formData.owner_id || "",
        country: formData.country || "99",
        state: formData.state || "",
        city: formData.city || "",
        about: formData.about || "",
        rating: rating.toString(),
        customer_id: customerId,
        employee_size: formData.employee_size || "",
        user_id: customer.customer_details?.id,
      }
      const cloneCompany = {
        ...formData,
        company_id: company_data?.id,
        email: emails[0]?.email || "",
        phone: contacts[0]?.phone || "",
        user_email: emails,
        user_phone: contacts,
        alternate_email: alternateEmails,
        business_type: formData.business_type || "PRIVATE",
        alternate_phone: alternateContacts,
        timezone: selectedTimezone,
        company_image: image?.[0] || null,
        old_company_image: company_data?.company_image || null,
        owner_id: formData.owner_id || "",
        country: formData.country || "99",
        state: formData.state || "",
        city: formData.city || "",
        about: formData.about || "",
        rating: rating.toString(),
        customer_id: company_data?.customer_id?.toString() || "",
        employee_size: formData.employee_size || "",
        user_login_id: customer.customer_details?.id,
      }

      const cloneCompanyFinalPayload = {
        ...cloneCompany,
        custom_fields,
      }
      const editCompanyFinalPayload = {
        ...editCompany,
        custom_fields,
      }

      // . API Call (Clone vs Update)
      let data
      if (isCloneClicked) {
        data = await PostRequest(
          `company/createCompany`,
          cloneCompanyFinalPayload,
          false,
          { headers },
          extras,
        )
      } else {
        data = await PutRequest(
          `company/updateCompany/${company_data?.id}`,
          editCompanyFinalPayload,
          { headers },
          extras,
        )
      }

      //  Handle response
      if (data.status === "success") {
        if (isCloneClicked) {
          setIsCloneClicked(false)
        }
        if (findData === false) {
          // run fetchData
          fetchData && fetchData()
          setFindData(true)
        } else if (findData === undefined) {
          // run refetch
          fetchData && fetchData()
        }

        setShow(false)

        toast.success(data?.message || "Company Updated Successfully!")
      } else {
        toast.error(data?.message || "Error updating company")
      }
    } catch (error) {
      console.error("Update error:", error)
      toast.error("Something went wrong while updating.")
    }
  }

  // Reset form when closing
  const handleClose = () => {
    if (isDataFetching) return
    if (isCloneClicked && setIsCloneClicked(false)) reset()
    setShow(false)
  }


  const shouldShowField = (field) => {
    if (!showOnlyRequired) return true; // Show all fields when toggle is off

    // When toggle is on, show only fields that are required OR important
    return field.is_required === "yes" || field.is_important === "yes";
  };
  return (
    <Offcanvas
      className="createContactOffcanvas"
      placement="end"
      backdrop="static"
      enforceFocus={true}
      show={show}
      onHide={() => {
        if (!isDataFetching) handleClose()
      }}
      style={{ zIndex: 1111 }}
      keyboard={!isDataFetching}
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <div className="d-flex align-items-center gap-2">
          <div className="pt-0 ps-4">
            <Offcanvas.Title
              style={{ color: "#0791ff" }}
              className="addEnquiryHead"
            >
              {" "}
              {isCloneClicked ? "Clone Company" : "Edit Company"}
            </Offcanvas.Title>
            <p
              style={{ fontSize: "14px", color: "#b26792", lineHeight: 1.1 }}
              className=" text-secondary text-nowrap mb-0"
            >
              Modify Company details, Mandatory fields must required.
            </p>
          </div>
        </div>
        <div
          className="d-flex justify-content-end align-items-center"
          style={{ width: "70%", border: "none" }}
        >
          <div type="button" className="form-check form-switch">
            <input
              className="form-check-input"
              checked={showOnlyRequired}
              onChange={() => setShowOnlyRequired((prev) => !prev)}
              type="checkbox"
              role="switch"
              id="switchCheckChecked"
            />
            <span
              className="form-check-label text-secondary"
              htmlFor="switchCheckChecked"
              style={{
                fontSize: "14px",
              }}
            >
              Show & Hide Important Field
            </span>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body
        style={{
          position: "relative",
          pointerEvents: isDataFetching ? "none" : "auto",
        }}
      >
        {isDataFetching && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.7)",
              zIndex: 1050,
              pointerEvents: "all",
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div style={{ display: "flex", height: "100%" }}>
          {/* Main Form */}
          <Form
            className="form-container w-100 pe-0 ps-0"
            onSubmit={handleSubmit(handleFormSubmit)}
            style={{
              margin: 0,
              overflowY: "auto",
              height: "100%",
              padding: "20px", // uniform padding on all sides
              position: "relative",
              width: "100%", // ensure full width
              boxSizing: "border-box", // include padding in width
              opacity: isDataFetching ? 0.6 : 1,
            }}
          >
            {/* Basic Information Section */}
            <div style={{ paddingLeft: 0, paddingTop: 10 }}>
              <h4 id="BasicInformation" className="offcanvasSecHeading">
                Basic Information
              </h4>
              {/* Company Name */}
              <Row className="ms-0 w-100 mt-4">

                {/* Company Name Field */}
                <Col md={6}>
                  <Form.Group
                    className="mb-3 focus-z-index position-relative"
                    controlId="formCompanyName"
                  >
                    <Form.Label
                      style={{ marginBottom: 1 }}
                      className="fw-lighter"
                    >
                      Company Name <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        {...register("company_name", { required: true })}
                        placeholder="Enter Company Name"
                        isInvalid={
                          !!errors.company_name || duplicateCompanyMessage
                        }
                      />
                    </InputGroup>
                    {errors.company_name && (
                      <p className="text-danger mt-1">
                        {errors.company_name.message}
                      </p>
                    )}
                    {duplicateCompanyMessage && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        <IconCircleX stroke={2} size={14} className="me-1" />
                        {duplicateCompanyMessage}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* company owner */}
                <Col md={6}>
                  <Form.Group
                    className="mb-3 focus-z-index position-relative"
                    controlId="formOwner"
                  >
                    <Form.Label
                      style={{ marginBottom: 1 }}
                      className="fw-lighter"
                    >
                      Owner <span className="text-danger">*</span>
                    </Form.Label>

                    <Controller
                      name="owner_id"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => {
                        const ownerOptions =
                          leadOwner?.map((lead) => ({
                            value: lead.leadId.toString(),
                            label: lead.leadName,
                          })) || []

                        return (
                          <Select
                            {...field}
                            styles={customStyles}
                            className="basic-single w-100"
                            options={ownerOptions}
                            value={
                              ownerOptions.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                            onChange={(selectedOption) => {
                              field.onChange(
                                selectedOption ? selectedOption.value : "",
                              )
                            }}
                            isDisabled={!isOwnerSame}
                            isSearchable
                            isClearable
                            placeholder="Select Owner"
                            noOptionsMessage={() => "No owners available"}
                            classNamePrefix="react-select"
                          />
                        )
                      }}
                    />

                    {errors.owner_id && (
                      <p
                        style={{ fontSize: "12px" }}
                        className="text-danger mt-1"
                      >
                        Owner is required
                      </p>
                    )}
                  </Form.Group>
                </Col>

                {!showOnlyRequired && (<>
                  {/* industry type */}
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formIndustry"
                    >
                      <Form.Label style={{ marginBottom: 1 }}>
                        Industry
                      </Form.Label>

                      <Controller
                        name="industry"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            styles={customStyles}
                            className="basic-single w-100"
                            options={industryOptions}
                            value={
                              industryOptions.find(
                                (option) => option.value === field.value
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value || "")
                            }
                            classNamePrefix="react-select"
                            isSearchable
                            isClearable
                            placeholder="Select Industry"
                            noOptionsMessage={() => "No industries available"}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>

                  {/* business type */}
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formBusinessType"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Business Type
                      </Form.Label>

                      <Controller
                        name="business_type"
                        control={control}
                        rules={{ required: "Business type is required" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            styles={customStyles}
                            options={[
                              {
                                value: "PRIVATE",
                                label: "Private Limited Company",
                              },
                              {
                                value: "PUBLIC",
                                label: "Public Limited Company",
                              },
                              {
                                value: "LLP",
                                label: "Limited Liability Partnership",
                              },
                              {
                                value: "PROPRIETORSHIP",
                                label: "Proprietorship",
                              },
                            ]}
                            placeholder="Select"
                            classNamePrefix="react-select"
                            isClearable
                            isSearchable
                            // 👇 force correct binding
                            value={
                              field.value
                                ? {
                                  value: field.value,
                                  label: {
                                    PRIVATE: "Private Limited Company",
                                    PUBLIC: "Public Limited Company",
                                    LLP: "Limited Liability Partnership",
                                    PROPRIETORSHIP: "Proprietorship",
                                  }[field.value],
                                }
                                : null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : "")
                            }
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                </>)}

              </Row>


              {!showOnlyRequired && (
                <Row className="ms-0 w-100">

                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formEmployeeSize"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Employee Size
                      </Form.Label>

                      <Controller
                        name="employee_size"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            styles={customStyles}
                            className="basic-single w-100"
                            options={employeeSizeOptions}
                            value={
                              employeeSizeOptions.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                            onChange={(selectedOption) => {
                              field.onChange(
                                selectedOption ? selectedOption.value : "",
                              )
                            }}
                            isSearchable
                            isClearable
                            placeholder="Select Employee Size"
                            noOptionsMessage={() => "No sizes available"}
                            classNamePrefix="react-select"
                          />
                        )}
                      />

                      {errors.employee_size && (
                        <p
                          style={{ fontSize: "12px" }}
                          className="text-danger mt-1"
                        >
                          Employee size is required
                        </p>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Website Field */}
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formWebsite"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Website
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          {...register("website")}
                          placeholder="Enter URL"
                        />
                        {errors.website && (
                          <Form.Control.Feedback type="invalid">
                            {errors.website.message}
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>

                </Row>)}
            </div>
            <hr />

            {/* Communication Section */}
            {!showOnlyRequired && (<>
              <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                <h4 id="BasicInformation" className="offcanvasSecHeading">
                  Communication
                </h4>
                <div className=" d-flex flex-column w-100 ms-0">
                  <Row className="w-100 ms-0 mt-3">
                    {/* Primary Email */}
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formEmail"
                      >
                        {emails.map((email, index) => {
                          const isDuplicate =
                            emails.filter(
                              (e) => e.email === email.email && e.email !== "",
                            ).length > 1
                          return (
                            <div key={index}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                <div
                                  style={{ width: "200px", position: "relative" }}
                                  className="focus-z-index"
                                >
                                  <Form.Label
                                    style={{ marginBottom: 1 }}
                                    className=""
                                  >
                                    Email
                                  </Form.Label>

                                  <Select
                                    classNamePrefix="react-select"
                                    options={[
                                      { value: "Office", label: "Office" },
                                      { value: "Personal", label: "Personal" },
                                    ]}
                                    value={{
                                      value: email.email_type || "Office",
                                      label: email.email_type || "Office",
                                    }}
                                    onChange={(selectedOption) =>
                                      handleEmailChange(
                                        "email_type",
                                        selectedOption.value,
                                        index,
                                      )
                                    }
                                    styles={customStyles}
                                  />
                                </div>
                                <InputGroup>
                                  <Form.Label
                                    style={{ marginBottom: 1 }}
                                    className=""
                                  >
                                    Email Address
                                  </Form.Label>
                                  <Form.Control
                                    style={{
                                      height: "40px",
                                      border: "1px solid #c5c5c5",
                                    }}
                                    type="email"
                                    value={email?.email}
                                    onChange={(e) =>
                                      handleEmailChange(
                                        "email",
                                        e.target.value,
                                        index,
                                      )
                                    }
                                    placeholder="ex: abc@gmail.com"
                                    className={
                                      isDuplicate ? "border-danger" : ""
                                    }
                                  />
                                </InputGroup>
                                {emails.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteEmail(index)}
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      border: "0.4px solid red",
                                      borderRadius: "50px",
                                      cursor: "pointer",
                                      marginLeft: "5px",
                                    }}
                                  >
                                    <IconTrash
                                      stroke={2}
                                      style={{ stroke: "red" }}
                                      className="w-3 h-3"
                                    />
                                  </button>
                                )}
                              </div>
                              {isDuplicate && (
                                <p
                                  className="text-danger"
                                  style={{ fontSize: "12px", marginTop: "2px" }}
                                >
                                  Duplicate email
                                </p>
                              )}
                              {emailValidationErrors.includes(index) && (
                                <p
                                  className="text-danger"
                                  style={{ fontSize: "12px", marginTop: "2px" }}
                                >
                                  Invalid email format
                                </p>
                              )}
                              {duplicateEmailMessage && index === 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                    gap: 2,
                                    paddingTop: "4px",
                                  }}
                                  className="text-danger"
                                >
                                  <IconCircleX
                                    stroke={2}
                                    style={{ width: "14px", height: "14px" }}
                                  />
                                  {duplicateEmailMessage}
                                </div>
                              )}
                            </div>
                          )
                        })}
                        <button
                          type="button"
                          onClick={handleAddEmail}
                          style={{
                            fontSize: "13px",
                            border: 0,
                            color: "#777",
                            background: "none",
                          }}
                        >
                          Add Email
                          <IconPlus
                            stroke={2}
                            style={{
                              width: "15px",
                              height: "15px",
                              border: "none",
                            }}
                          />
                        </button>
                      </Form.Group>
                    </Col>

                    {/* Primary Contact */}
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formContact"
                      >
                        {contacts.map((contact, index) => {
                          const isDuplicate =
                            contacts.filter(
                              (c) =>
                                c.phone === contact.phone &&
                                c.country_code === contact.country_code &&
                                contact.phone !== "",
                            ).length > 1

                          return (
                            <div key={index}>
                              <div
                                className="mb-2"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                <div
                                  style={{ width: "200px", position: "relative" }}
                                  className="focus-z-index"
                                >
                                  <Form.Label
                                    style={{ marginBottom: 1 }}
                                    className=""
                                  >
                                    Contact
                                  </Form.Label>

                                  <Select
                                    classNamePrefix="react-select"
                                    options={[
                                      { value: "Mobile", label: "Mobile" },
                                      { value: "Work", label: "Work" },
                                      { value: "Home", label: "Home" },
                                      { value: "Personal", label: "Personal" },
                                    ]}
                                    value={{
                                      value: contact.phone_type || "Mobile",
                                      label: contact.phone_type || "Mobile",
                                    }}
                                    onChange={(selectedOption) =>
                                      handleContactChange(
                                        "phone_type",
                                        selectedOption.value || "Mobile",
                                        index,
                                      )
                                    }
                                    styles={{
                                      ...customStyles,
                                    }}
                                  />
                                </div>
                                <InputGroup>
                                  <Form.Label>
                                    Mobile Number
                                  </Form.Label>
                                  <PhoneInput
                                    country={"in"}
                                    countryCodeEditable={false}
                                    buttonStyle={{ border: "none" }}
                                    inputStyle={{
                                      width: "320px",
                                      height: "40px",
                                      border: "none",
                                    }}
                                    containerStyle={{
                                      border: `${isDuplicate
                                        ? "1px solid #dc3545"
                                        : "1px solid #c5c5c5"
                                        }`,
                                      borderRadius: "5px",
                                    }}
                                    value={`${contact.country_code}${contact.phone}`}
                                    onChange={(
                                      value,
                                      country,
                                      e,
                                      rawInputValue,
                                    ) => {
                                      const countryCode = country.dialCode
                                      const rawNum =
                                        rawInputValue?.replace(/\D/g, "") || ""
                                      handleContactChange(
                                        "phone",
                                        rawNum.slice(countryCode.length),
                                        index,
                                        {
                                          country_code: `+${countryCode}`,
                                        },
                                      )
                                    }}
                                    classNamePrefix="react-select"
                                  // inputProps={{
                                  //   required: index === 0,
                                  //   name: "contact",
                                  // }}
                                  />
                                </InputGroup>
                                {contacts.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteContact(index)}
                                    style={{
                                      width: "14px",
                                      height: "14px",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      border: "none",
                                      borderRadius: "50px",
                                      cursor: "pointer",
                                      marginLeft: "0px",
                                      padding: 0,
                                      background: "none",
                                    }}
                                  >
                                    <IconTrash
                                      stroke={2}
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        stroke: "red",
                                      }}
                                    />
                                  </button>
                                )}
                              </div>
                              {isDuplicate && (
                                <p
                                  className="text-danger"
                                  style={{ fontSize: "12px", marginTop: "2px" }}
                                >
                                  Duplicate contact
                                </p>
                              )}
                              {contactValidationErrors.includes(index) && (
                                <p
                                  className="text-danger"
                                  style={{ fontSize: "12px", marginTop: "2px" }}
                                >
                                  Invalid phone number
                                </p>
                              )}
                              {duplicatePhoneMessage && index === 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                    gap: 2,
                                    paddingTop: "4px",
                                  }}
                                  className="text-danger"
                                >
                                  <IconCircleX
                                    stroke={2}
                                    style={{ width: "14px", height: "14px" }}
                                  />
                                  {duplicatePhoneMessage}
                                </div>
                              )}
                            </div>
                          )
                        })}
                        <button
                          type="button"
                          onClick={handleAddContact}
                          style={{
                            fontSize: "13px",
                            border: 0,
                            color: "#777",
                            background: "none",
                          }}
                        >
                          Add Contact{" "}
                          <IconPlus
                            stroke={2}
                            style={{
                              width: "15px",
                              height: "15px",
                              border: "none",
                            }}
                          />
                        </button>
                      </Form.Group>
                    </Col>

                    {/* Timezone Picker */}

                    <Col md={12}>
                      <Form.Group
                        className="mb-3 focus-z-index position-relative"
                        controlId="formTimezone"
                      >
                        <div className="d-flex flex-column gap-1">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className="fw-lighter"
                          >
                            Select Timezone
                          </Form.Label>
                          <div style={{ fontSize: "14px" }}>
                            <TimezoneSelect
                              styles={customStyles}
                              timezones={{
                                ...allTimezones,
                                "America/Lima": "Pittsburgh",
                                "Europe/Berlin": "Frankfurt",
                              }}
                              value={selectedTimezone}
                              onChange={setSelectedTimezone}
                              classNamePrefix="react-select"
                            />
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </div>
              <hr /></>)}

            {/* Location Section */}
            {!showOnlyRequired && (<>
              <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                <h4 id="BasicInformation" className="offcanvasSecHeading">
                  Location
                </h4>
                {/* Address */}
                <Row className="ms-0 w-100 mt-4">
                  <Col md={12}>
                    <Form.Group
                      className="mb-3 d-flex flex-column focus-z-index position-relative"
                      controlId="formAddress"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter"
                      >
                        Address
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as={"textarea"}
                          {...register("address")}
                          rows={3}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
                {/* Country and State */}
                <Row className="ms-0 w-100">
                  {/* <Col md={6}>
                  <Form.Group
                    className="mb-3  d-flex flex-column focus-z-index position-relative"
                    controlId="formCountry"
                  >
                    <Form.Label
                      style={{ marginBottom: "2px", marginTop: "2px" }}
                      className="fw-lighter "
                    >
                      Select Country
                    </Form.Label>
                    <div>
                      <Select
                        styles={customStyles}
                        className="basic-single w-100"
                        classNamePrefix="react-select"
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        options={country}
                        isClearable={true}
                        isSearchable={true}
                        placeholder="Select Country"
                      />
                    </div>
                  </Form.Group>
                </Col> */}

                  <Col md={6}>
                    <Form.Group
                      className="mb-3 d-flex flex-column focus-z-index position-relative"
                      controlId="formCountry"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter"
                      >
                        Select Country
                      </Form.Label>

                      <div>
                        <Controller
                          name="country"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              styles={customStyles}
                              className="basic-single w-100"
                              classNamePrefix="react-select"
                              options={country}
                              value={
                                country.find(
                                  (option) => option.value === field.value,
                                ) || null
                              }
                              onChange={(selectedOption) => {
                                field.onChange(
                                  selectedOption ? selectedOption.value : "",
                                )
                                handleCountryChange(selectedOption)
                              }}
                              isClearable
                              isSearchable
                              placeholder="Select Country"
                            />
                          )}
                        />
                      </div>

                      {errors.country && (
                        <p
                          style={{ fontSize: "12px" }}
                          className="text-danger mt-1"
                        >
                          Country is required
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3  d-flex flex-column focus-z-index position-relative"
                      controlId="formState"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter "
                      >
                        Select State
                      </Form.Label>
                      <div>
                        <Select
                          styles={customStyles}
                          className="basic-single w-100"
                          classNamePrefix="react-select"
                          value={selectedState}
                          onChange={handleStateChange}
                          options={states}
                          isClearable={true}
                          isSearchable={true}
                          placeholder="Select State"
                        // isDisabled={!selectedCountry}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="ms-0 w-100">
                  <Col md={6}>
                    <Form.Group
                      className="mb-3  d-flex flex-column focus-z-index position-relative"
                      controlId="formCity"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter "
                      >
                        Select City
                      </Form.Label>
                      <div>
                        <Select
                          styles={customStyles}
                          className="basic-single w-100"
                          classNamePrefix="react-select"
                          value={selectedCity}
                          onChange={handleCityChange}
                          options={city}
                          isClearable={true}
                          isSearchable={true}
                          placeholder="Select City"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 d-flex flex-column focus-z-index position-relative"
                      controlId="formZipcode"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter"
                      >
                        Enter Zipcode
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          {...register("zipcode")}
                          placeholder="Enter Zipcode"
                          onKeyPress={(e) => {
                            // Allow only numbers
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault()
                            }
                          }}
                          onPaste={(e) => {
                            // Prevent pasting non-numeric content
                            const pasteData = e.clipboardData.getData("text")
                            if (!/^\d+$/.test(pasteData)) {
                              e.preventDefault()
                            }
                          }}
                        />
                      </InputGroup>
                      {errors.zipcode && (
                        <p className="text-danger mt-1">
                          {errors.zipcode.message}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <hr /></>)}
            {/* Social Links Section */}
            {!showOnlyRequired && (<>
              <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                <h4 id="BasicInformation" className="offcanvasSecHeading">
                  Social
                </h4>
                {/* Facebook and LinkedIn */}
                <Row className="ms-0 w-100 mt-4">
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formFacebook"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Facebook
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="url"
                          {...register("facebook")}
                          placeholder="Enter URL"
                        />
                        {errors.facebook && (
                          <Form.Control.Feedback type="invalid">
                            {errors.facebook.message}
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formLinkedin"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        LinkedIn
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="url"
                          {...register("linkedin")}
                          placeholder="Enter URL"
                        />
                        {errors.linkedin && (
                          <Form.Control.Feedback type="invalid">
                            {errors.linkedin.message}
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
                {/* Instagram and Twitter */}
                <Row className="ms-0 w-100">
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formInstagram"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Instagram
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="url"
                          {...register("instagram")}
                          placeholder="Enter URL"
                        />
                        {errors.instagram && (
                          <Form.Control.Feedback type="invalid">
                            {errors.instagram.message}
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formTwitter"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter"
                      >
                        Twitter
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="url"
                          {...register("twitter")}
                          placeholder="Enter URL"
                        />
                        {errors.twitter && (
                          <Form.Control.Feedback type="invalid">
                            {errors.twitter.message}
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <hr /></>)}
            {/* About and Rating Section */}
            {!showOnlyRequired && (
              <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                <h4 id="BasicInformation" className="offcanvasSecHeading">
                  About & Rating
                </h4>
                {/* About Company */}
                <Row className="ms-0 w-100 mt-4">
                  <Col md={12}>
                    <Form.Group
                      className="mb-3 d-flex flex-column focus-z-index position-relative"
                      controlId="formAbout"
                    >
                      <Form.Label
                        style={{ marginBottom: "2px", marginTop: "2px" }}
                        className="fw-lighter"
                      >
                        About Company
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as={"textarea"}
                          {...register("about")}
                          placeholder="Tell us about your company"
                          rows={4}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
                {/* Star Rating */}
                <Row className="ms-0 w-100">
                  <Col md={12}>
                    <Form.Group
                      className="mb-3 focus-z-index position-relative"
                      controlId="formRating"
                    >
                      <Form.Label
                        style={{ marginBottom: 1 }}
                        className="fw-lighter "
                      >
                        Rating <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex align-items-center gap-1 pt-4 ps-2">
                        <StarRating
                          rating={rating}
                          onRatingChange={handleRatingChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* <div style={{ height: "80px", marginBottom: "40px" }}></div> */}
              </div>)}

            {/* grouped custom fields */}

            <div style={{ marginBottom: "100px", marginLeft: "-20px" }}>
              {grouped &&
                grouped.length !== 0 &&
                grouped.map((g) => {
                  const capitalizedName = g.group_name.replace(
                    /\b\w/g,
                    (char) => char.toUpperCase(),
                  )
                  return (
                    <div
                      key={g.id}
                      style={{ marginBottom: "100px" }}
                    >
                      {g.groupFields?.length !== 0 && <hr />}
                      <div className="offcanvasSections">
                        <h4
                          id={capitalizedName}
                          className="offcanvasSecHeading"
                        >
                          {g.groupFields?.length !== 0 && capitalizedName}
                        </h4>
                        <Row className="w-100 ms-0 pt-3">
                          {g.groupFields?.length !== 0 &&
                            g.groupFields?.map((field) => {
                              return (
                                <React.Fragment key={field.id}>
                                  {field.label_type === "text" && (
                                    <Col md={6}>
                                      <Text
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "monetary" && (
                                    <Col md={6}>
                                      <Text
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "largeText" && (
                                    <Col md={6}>
                                      <Text
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "numerical" && (
                                    <Col md={6}>
                                      <Text
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "address" && (
                                    <Col md={6}>
                                      <Text
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "single-option" && (
                                    <Col md={6}>
                                      <SelectOpt
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                        options={
                                          field.option !== null && field.option
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "user" && (
                                    <Col md={6}>
                                      <SelectOpt
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type="single-option"
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                        options={leadOwner.map((lead) => ({
                                          value: lead.leadId,
                                          label: lead.leadName,
                                        }))}
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "person" && (
                                    <Col md={6}>
                                      <SelectOpt
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type="single-option"
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                        options={
                                          allContacts.length > 0
                                            ? allContacts
                                            : []
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "organization" && (
                                    <Col md={6}>
                                      <SelectOpt
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type="single-option"
                                        control={control}
                                        rules={{
                                          required: `${field.label_name} is required`,
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      // options={company}
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "time" && (
                                    <Col md={6}>
                                      <Time
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "time-range" && (
                                    <Col md={6}>
                                      <Time
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required:
                                            field.is_required === "yes" &&
                                            "Working hours are required",
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}

                                  {field.label_type === "phone" && (
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required:
                                            field.is_required === "yes" &&
                                            "Phone number is required",
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        validatePhoneNumber={
                                          validatePhoneNumber
                                        }
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "date" && (
                                    <Col md={6}>
                                      <DateField
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        rules={{
                                          required:
                                            field.is_required === "yes" &&
                                            "Date is required",
                                        }}
                                        isRequired={field.is_required === "yes"}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "date range" && (
                                    <Col md={6}>
                                      <DateField
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        // rules={{
                                        //   required:
                                        //     field.is_required === "yes" &&
                                        //     "Date Range number is required",
                                        //   validate: {
                                        //     validRange: (value) => {
                                        //       if (!value?.start || !value?.end)
                                        //         return true // Let required handle empty values
                                        //       return (
                                        //         value.start <= value.end ||
                                        //         "End date must be after or same as start date"
                                        //       )
                                        //     },
                                        //   },
                                        // }}
                                        isImportant={
                                          field.is_important === "yes"
                                        }
                                        isRequired={field.is_required === "yes"}
                                      />
                                    </Col>
                                  )}
                                  {field.label_type === "multiple-option" && (
                                    <Col md={6}>
                                      <MultipleOpt
                                        name={field.id}
                                        label_name={field.label_name}
                                        label_type={field.label_type}
                                        control={control}
                                        isRequired={
                                          field.is_required === "yes"
                                            ? true
                                            : false
                                        }
                                        isImportant={
                                          field.is_important === "yes"
                                            ? true
                                            : false
                                        }
                                        options={
                                          field.option &&
                                          JSON.parse(field.option)
                                        }
                                        rules={{
                                          required:
                                            field.is_required === "yes" &&
                                            "Teams is required",
                                        }}
                                      />
                                    </Col>
                                  )}
                                </React.Fragment>
                              )
                            })}
                        </Row>
                      </div>
                    </div>
                  )
                })}

              {ungrouped?.length !== 0 && <hr />}
              <Row className="w-100 ms-0 mt-3" style={{ paddingLeft: "20px" }}>
                {ungrouped &&
                  ungrouped.length !== 0 &&
                  ungrouped.map((field) => {
                    return (
                      <React.Fragment key={field.id}>
                        {field.label_type === "text" && (
                          <Col md={6}>
                            <Text
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            />
                          </Col>
                        )}
                        {field.label_type === "monetary" && (
                          <Col md={6}>
                            <Text
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            />
                          </Col>
                        )}
                        {field.label_type === "largeText" && (
                          <Col md={6}>
                            <Text
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            />
                          </Col>
                        )}
                        {field.label_type === "numerical" && (
                          <Col md={6}>
                            <Text
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            />
                          </Col>
                        )}
                        {field.label_type === "address" && (
                          <Col md={6}>
                            <Text
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            />
                          </Col>
                        )}
                        {field.label_type === "single-option" && (
                          <Col md={6}>
                            <SelectOpt
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                              options={field.option !== null && field.option}
                            />
                          </Col>
                        )}
                        {field.label_type === "user" && (
                          <Col md={6}>
                            <SelectOpt
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              control={control}
                              labelClass="mb-3 focus-z-index position-relative"
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                              options={leadOwner}
                            />
                          </Col>
                        )}
                        {field.label_type === "person" && (
                          <Col md={6}>
                            <SelectOpt
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              control={control}
                              labelClass="mb-3 focus-z-index position-relative"
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                              options={
                                allContacts.length > 0 ? allContacts : []
                              }
                            />
                          </Col>
                        )}
                        {field.label_type === "organization" && (
                          <Col md={6}>
                            <SelectOpt
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required: `${field.label_name} is required`,
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={field.is_important === "yes"}
                            //  options={company}
                            />
                          </Col>
                        )}
                        {field.label_type === "time" && (
                          <Col md={6}>
                            <Time
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              isRequired={field.is_required === "yes"}
                              isImportant={
                                field.is_important === "yes"
                              }
                            />
                          </Col>
                        )}
                        {field.label_type === "time-range" && (
                          <Col md={6}>
                            <Time
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              // rules={{
                              //   required:
                              //     field.is_required === "yes" &&
                              //     "Working hours are required",
                              //   validate: {
                              //     validRange: (value) => {
                              //       if (!value?.start || !value?.end)
                              //         return true // Let required handle empty values
                              //       return (
                              //         value.start < value.end ||
                              //         "End time must be after start time"
                              //       )
                              //     },
                              //   },
                              // }}
                              isImportant={
                                field.is_important === "yes"
                              }
                              isRequired={field.is_required === "yes"}
                            />
                          </Col>
                        )}

                        {field.label_type === "phone" && (
                          <Col md={6}>
                            <PhoneNumberInput
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required:
                                  field.is_required === "yes" &&
                                  "Phone number is required",
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={
                                field.is_important === "yes"
                              }
                              validatePhoneNumber={validatePhoneNumber}
                            />
                          </Col>
                        )}
                        {field.label_type === "date" && (
                          <Col md={6}>
                            <DateField
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required:
                                  field.is_required === "yes" &&
                                  "Date is required",
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={
                                field.is_important === "yes"
                              }
                            />
                          </Col>
                        )}
                        {field.label_type === "date range" && (
                          <Col md={6}>
                            <DateField
                              name={field.id}
                              label_name={field.label_name}
                              label_type={field.label_type}
                              labelClass="mb-3 focus-z-index position-relative"
                              control={control}
                              rules={{
                                required:
                                  field.is_required === "yes" &&
                                  "Date Range number is required",
                                validate: {
                                  validRange: (value) => {
                                    if (!value?.start || !value?.end)
                                      return true // Let required handle empty values
                                    return (
                                      value.start <= value.end ||
                                      "End date must be after or same as start date"
                                    )
                                  },
                                },
                              }}
                              isRequired={field.is_required === "yes"}
                              isImportant={
                                field.is_important === "yes"
                              }
                            />
                          </Col>
                        )}
                      </React.Fragment>
                    )
                  })}
              </Row>
            </div>

            {/* Form Actions */}
            <div
              style={{
                position: "fixed",
                width: "60%",
                bottom: 0,
                height: "80px",
                zIndex: 50,
                right: 0,
                borderTop: "1px solid #e0eaff",
                backgroundColor: "white",
                paddingRight: "20px",
              }}
            >
              <div className="d-flex justify-content-end gap-2 pt-4">
                <CancelBtn
                  type={'button'}
                  onClick={handleClose}
                // submitClicked={loading}
                />

                <SubmitBtn
                  type={'submit'}
                  title={isCloneClicked ? "Clone" : "Update"}
                // loading={loading}
                />
              </div>
            </div>
          </Form>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default EditCompanyOffcanvas;
